import { createSlice } from '@reduxjs/toolkit';
import { activityInitialState, activityReducers } from '../_shared/activityState';

/**
 * Teams slice — scoped by org + site.
 *
 * Data structure:
 *   state.data[orgID][siteID] = { data: [...], loading: false }
 */

const initialState = {
	activity: { ...activityInitialState },
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	searchable: true,
	error: null,

	// ✅ Loading states (global — CRUD transient state)
	loadingAddMembersTeam: false,
	teamMembersLoading: false,
	addTeamLoading: false,
	updateTeamLoading: false,
	deleteTeamLoading: false,
	deleteTeamMemberLoading: false
};

// Ensures the bucket exists at data[orgID][key]
const ensureBucket = (state, orgID, siteID) => {
	const key = siteID;
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) state.data[orgID][key] = { data: [], loading: false };
	return key;
};

// Patches a single team inside the active scope bucket
const patchTeam = (state, orgID, siteID, teamID, patcher) => {
	const key = siteID;
	const bucket = state.data[orgID]?.[key];
	if (!bucket?.data) return;
	bucket.data = bucket.data.map((t) => (t.teamID === teamID ? patcher(t) : t));
};

const teamsSlice = createSlice({
	name: 'teams',
	initialState,
	reducers: {
		...activityReducers,
		// ── List load (scoped) ──────────────────────────────────────────

		// Load Teams — trigger (saga listens, reducer is no-op)
		loadTeams: () => {},

		// Sets loading on the target bucket. No saga listens (prevents loop).
		loadTeamsStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},

		// Replaces bucket data — backend is source of truth for this scope.
		loadTeamsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key] = { data, loading: false, loaded: true };
		},

		loadTeamsError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID;
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.error = error;
		},

		// REFRESH (silent — replaces data without loading flash, used after inline edits)
		refreshTeams: () => {},
		refreshTeamsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			const existing = state.data[orgID][key].data || [];

			// Preserve members sub-entity from existing teams
			const membersMap = {};
			existing.forEach((t) => {
				if (t.members) membersMap[t.teamID] = t.members;
			});

			state.data[orgID][key].data = data.map((t) => ({
				...t,
				...(membersMap[t.teamID] ? { members: membersMap[t.teamID] } : {})
			}));
		},

		// ── Sub-entity patchers ─────────────────────────────────────────

		// Load All Teams with Members — patches members onto each team in the active bucket
		loadAllTeamsMembers(state) {
			state.error = null;
		},
		loadAllTeamsMembersSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = siteID;
			const bucket = state.data[orgID]?.[key];
			if (!bucket?.data) return;

			//  data is an object with teamID as the key and an array of personIDs as the value
			//  we need to add the personIDs to each team's members
			bucket.data = bucket.data.map((team) => {
				const members = data[team.teamID] || [];
				return { ...team, members };
			});
		},
		loadAllTeamsMembersError(state, action) {
			state.error = action.payload;
		},

		// Load Team Members — patches members for a single team
		loadTeamMembers(state) {
			state.teamMembersLoading = true;
			state.error = null;
		},
		loadTeamMembersSuccess(state, action) {
			const { orgID, siteID, teamID, data } = action.payload;
			patchTeam(state, orgID, siteID, teamID, (t) => ({ ...t, members: data }));
			state.teamMembersLoading = false;
		},
		loadTeamMembersError(state, action) {
			state.error = action.payload;
			state.teamMembersLoading = false;
		},

		// Silent refresh — same as loadTeamMembers but without loading flag
		refreshTeamMembers: () => {},
		refreshTeamMembersSuccess(state, action) {
			const { orgID, siteID, teamID, data } = action.payload;
			patchTeam(state, orgID, siteID, teamID, (t) => ({ ...t, members: data }));
		},

		// ── CRUD actions (global loading flags) ─────────────────────────

		// Add Team Member — the modal closes optimistically on dispatch; this flag
		// only drives the topbar in-flight indicator, so no success flag is needed.
		addTeamMember(state) {
			state.loadingAddMembersTeam = true;
			state.error = null;
		},
		addTeamMemberSuccess(state) {
			state.loadingAddMembersTeam = false;
		},
		addTeamMemberError(state, action) {
			state.error = action.payload;
			state.loadingAddMembersTeam = false;
		},

		// Delete Team Member
		deleteTeamMember(state) {
			state.deleteTeamMemberLoading = true;
		},
		deleteTeamMemberSuccess(state) {
			state.deleteTeamMemberLoading = false;
		},
		deleteTeamMemberError(state, action) {
			state.error = action.payload;
			state.deleteTeamMemberLoading = false;
		},

		// Add Team — the modal closes optimistically on dispatch; this flag only
		// drives the topbar in-flight indicator, so no success flag is needed.
		addTeam(state) {
			state.addTeamLoading = true;
			state.error = null;
		},
		addTeamSuccess(state) {
			state.addTeamLoading = false;
		},
		addTeamError(state, action) {
			state.error = action.payload;
			state.addTeamLoading = false;
		},

		// Update Team
		updateTeam(state) {
			state.updateTeamLoading = true;
			state.error = null;
		},
		updateTeamSuccess(state) {
			state.updateTeamLoading = false;
		},
		updateTeamError(state, action) {
			state.error = action.payload;
			state.updateTeamLoading = false;
		},

		// Delete Team — the modal closes optimistically on dispatch; this flag only
		// drives the topbar in-flight indicator, so no success/reset flag is needed.
		deleteTeam(state) {
			state.deleteTeamLoading = true;
			state.error = null;
		},
		deleteTeamSuccess(state) {
			state.deleteTeamLoading = false;
		},
		deleteTeamError(state, action) {
			state.error = action.payload;
			state.deleteTeamLoading = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

// Export actions
export const teamsActions = teamsSlice.actions;

// Export reducer
export default teamsSlice.reducer;
